import React, { FC, useState } from "react";
import { Box } from "@material-ui/core";
import PayGroupPeriodHeader from "Components/Pages/UserProfile/UserProfile/UserProfileTabs/StatementsTab/PayPeriodHeader/PayPeriodHeader";
import Summary from "Components/Pages/UserProfile/PayPeriodSummary/Summary";
import UserPayments from "Components/Pages/UserProfile/UserPayments/UserPayments";
import DailyPayPayments from "Components/Pages/UserProfile/DailyPayPayments/DailyPayPayments";
import { usePayPeriodQuery } from "./Hooks/usePayPeriodQuery";
import NegativeBalanceMessage from "Components/Pages/UserProfile/NegativeBalanceMessage/NegativeBalanceMessage";
import { DPProviderAccount, PayGroup } from "Shared/types/EmployeeProfiles";

type Props = {
  providerAccounts: DPProviderAccount[];
  employeeProfileId: string;
};

const StatementsTab: FC<Props> = ({ providerAccounts, employeeProfileId }) => {
  // Company IDs are sequential quoted numbers. Finding the most recently created
  // paygroup is equivalent to finding the paygroup with the largest integer parsed ID
  const mostRecentPayGroupIndex = providerAccounts.reduce((previousValue, currentProviderAccount, currentIndex) => {
    return +providerAccounts[previousValue].company.id < +currentProviderAccount.company.id
      ? currentIndex
      : previousValue;
  }, 0);

  // Store PayGroup Index and PayPeriod Index together so they can be set simultaneously
  const [payPeriodLocation, setPayPeriodLocation] = useState({
    payGroupIndex: mostRecentPayGroupIndex,
    payPeriodIndex: 0
  });

  const payGroups = providerAccounts.map(providerAccount => ({
    id: providerAccount.company.payGroup,
    providerAccountId: providerAccount.id,
    payPeriods: providerAccount.payPeriods?.items || [],
  })) as PayGroup[];

  const payGroupIndex = payPeriodLocation.payGroupIndex;
  const payPeriodIndex = payPeriodLocation.payPeriodIndex;

  const payPeriod = usePayPeriodQuery({
    payGroups,
    payGroupIndex,
    payPeriodIndex,
    employeeProfileId,
  });

  const onPayGroupIndexChanged = (index: number) => {
    setPayPeriodLocation({
      payGroupIndex: index,
      payPeriodIndex: 0,
    });
  };

  const onPayPeriodIndexChanged = (index: number) => {
    setPayPeriodLocation({
      payGroupIndex,
      payPeriodIndex: index
    });
  };
  const doShowNegativeBalanceMessage =
    typeof payPeriod.summary.remainderAmountCents === "number"
    && payPeriod.summary.remainderAmountCents < 0;

  const doShowSummary =
    !doShowNegativeBalanceMessage
    && typeof payPeriod.summary.netEarningsAmountCents === "number"
    && payPeriod.summary.netEarningsAmountCents > 0;

  return (
    <Box id="pay-period-info">
      <PayGroupPeriodHeader
        payGroupIndex={payGroupIndex}
        payPeriodIndex={payPeriodIndex}
        onPayGroupIndexChanged={onPayGroupIndexChanged}
        payGroups={payGroups.map(payGroup => payGroup.id)}
        onPayPeriodIndexChanged={onPayPeriodIndexChanged}
        payPeriods={payGroups[payGroupIndex].payPeriods}
      />
      <Box id="paycheck-info">
        {doShowNegativeBalanceMessage && <NegativeBalanceMessage />}
        {doShowSummary && <Summary {...payPeriod.summary} />}
        <UserPayments payments={payPeriod.employeePayments.items} />
        <DailyPayPayments payments={payPeriod.providerPayments.items} />
      </Box>
    </Box>
  );
};

export default StatementsTab;
